import React, { ReactNode, useMemo } from 'react';
import { Button, ButtonProps } from '@cp/ds/src/components/button';
import { Box } from '@cp/ds/src/components/box';
import { Stack } from '@cp/ds/src/components/stack';
import { useIsMobile } from '@cp/ds/src/hooks/useIsMobile';
import { AdvantageList, AdvantageTokens } from '@cp/entities/advantage';
import { ClientLastSeenText, days190strategy } from '@cp/entities/clientOnlineStatus';
import { Schema } from '@cp/ds/src/components/schema';
import { ContentSkeleton } from '@cp/ds/src/components/contentSkeleton';
import { Container } from '@cp/ds/src/components/container';
import useMediaQuery from '@mui/material/useMediaQuery';
import { AppTheme } from '@cp/ds';
import { ItemResumeCard, ResumeTabs } from 'src/features/resume';
import { Achievement, UserMainData } from 'src/entities/resume';
import { _experimentalComponentBuilder as componentBuilder } from 'src/shared/componentBuilder';
import { withTrackClick } from 'src/shared/tracking';
import { CatalogEvents } from 'src/features/catalog';
import { withAuth } from 'src/features/auth';
import { withConnectCatalogItemClick, withOrderNannyClick } from 'src/features/catalogItem';
import { withSubscriptionClick } from 'src/features/billing';
import { ClientSelection } from 'src/entities/client';
import { ClientType } from '@common/model/client/clientType';
import { useCatalogItemAdvantages, useProfilePageSchema } from '../../model';
import type { ResumeProps } from './resume';
import { ResumeFavorite } from './favorite';

const resumeButtonProps: ButtonProps = {
  color: 'gray',
  size: 'normal',
  sx: { flex: 1 },
  variant: 'primary',
};

const InviteButton = componentBuilder(Button)
  .composeWithArgs(withTrackClick, CatalogEvents.ItemInvite)
  .compose(withOrderNannyClick)
  .compose(withSubscriptionClick)
  .compose(withAuth)
  .buildWithProps(resumeButtonProps);

const WriteButton = componentBuilder(Button)
  .composeWithArgs(withTrackClick, CatalogEvents.ItemQuestion)
  .compose(withConnectCatalogItemClick)
  .compose(withAuth)
  .buildWithProps(resumeButtonProps);

export const ResumeV2: React.FC<ResumeProps> = ({ data, ...props }) => {
  const isMobile = useIsMobile();
  const jsonSchema = useProfilePageSchema(data);
  const isSmallScreen = useMediaQuery((theme: AppTheme) => theme.breakpoints.down('lg'));

  const SectionContainer = isMobile ? Container : Box;
  const onlineStatus = useMemo(() => {
    return props.showOnlineStatus && data?.clientProfile && <ClientLastSeenText profile={data?.clientProfile} strategy={days190strategy} />;
  }, [data?.clientProfile, props.showOnlineStatus]);

  const advantageList = useCatalogItemAdvantages(data);
  const advantages: ReactNode = useMemo(() => {
    const advantageProps = { bordered: true };
    return (
      <AdvantageList
        advantageProps={advantageProps}
        advantages={advantageList}
        sx={{ gap: 1, display: 'flex', flexWrap: 'wrap', [AdvantageTokens.colorBackground]: 'white' }}
      />
    );
  }, [advantageList]);

  if (props.isLoading) {
    return (
      <SectionContainer>
        <ContentSkeleton blocks={2} spacing={1} />
      </SectionContainer>
    );
  }

  if (!data) {
    return null;
  }

  const authSelection: ClientSelection = {
    careerId: data.careerId,
    clientType: ClientType.Employer,
  };

  return (
    <Stack sx={{ width: 1, pb: '20px' }}>
      <ItemResumeCard
        achievements={
          <>
            {data.clientProfile?.trainingCompletedAt && <Achievement type="school" />}
            {data.clientProfile?.isDocumentsChecked && <Achievement type="verified" />}
            {data.clientProfile?.isOnline && <Achievement type="online" />}
          </>
        }
        data={data}
        favoriteButton={
          isSmallScreen && <ResumeFavorite clientId={data.clientProfile?.id || 0} itemSx={{ width: '36px', height: '36px' }} />
        }
        onlineStatus={onlineStatus}
      />

      {isMobile && <UserMainData data={data} isMobile onlineStatus={onlineStatus} />}

      {isSmallScreen && (
        <Stack
          direction="row"
          spacing={1.5}
          sx={{ maxWidth: '480px', width: '100%', px: { xs: '12px', md: 0 }, mt: { xs: 0, md: '20px' } }}
        >
          <InviteButton catalogItemId={data.id} {...authSelection}>
            Позвать
          </InviteButton>
          <WriteButton catalogItemId={data.id} {...authSelection}>
            Написать
          </WriteButton>
        </Stack>
      )}

      <ResumeTabs advantages={advantages} data={data} />

      <Schema schema={jsonSchema} />
    </Stack>
  );
};
ResumeV2.displayName = 'widget/ResumeV2';
